if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.initDots();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.initThumbnailNavigation();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initDots() {
        this.dotButtons = this.elements.viewer.querySelectorAll('.slider-counter__link--dots');
        if (!this.dotButtons.length) return;

        this.elements.viewer.addEventListener('slideChanged', (event) => {
          this.setActiveDot(event.detail.currentPage);
        });

        this.dotButtons.forEach((button, index) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.goToSlide(index);
          });
        });
      }

      initThumbnailNavigation() {
        if (this.thumbnailNavInitialized) return;

        this.prevThumbnailButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.nextThumbnailButton = this.elements.thumbnails.querySelector('button[name="next"]');
        if (!this.prevThumbnailButton || !this.nextThumbnailButton) return;

        this.thumbnailNavInitialized = true;
        this.prevThumbnailButton.addEventListener('click', this.onThumbnailNavClick.bind(this), true);
        this.nextThumbnailButton.addEventListener('click', this.onThumbnailNavClick.bind(this), true);

        // SliderComponent disables arrows based on thumbnail strip scroll position.
        // Re-apply disabled state from the active main image whenever the strip moves.
        const bindScrollSync = () => {
          const slider = this.elements.thumbnails.slider;
          if (!slider) {
            requestAnimationFrame(bindScrollSync);
            return;
          }

          slider.addEventListener('scroll', () => {
            if (this.mql.matches && this.dataset.desktopLayout.includes('thumbnail')) {
              this.updateThumbnailNavButtons();
            }
          });
          this.updateThumbnailNavButtons();
        };

        bindScrollSync();
      }

      onThumbnailNavClick(event) {
        if (!this.mql.matches || !this.dataset.desktopLayout.includes('thumbnail')) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        this.cycleActiveMedia(direction);
      }

      getThumbnailItems() {
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (item) => item.clientWidth > 0
        );
      }

      cycleActiveMedia(direction) {
        const thumbnails = this.getThumbnailItems();
        if (thumbnails.length < 2) return;

        const activeIndex = thumbnails.findIndex((thumb) =>
          thumb.querySelector('button[aria-current="true"]')
        );
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = currentIndex + direction;

        if (nextIndex < 0 || nextIndex >= thumbnails.length) return;

        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      updateThumbnailNavButtons() {
        if (!this.prevThumbnailButton || !this.nextThumbnailButton) return;

        const thumbnails = this.getThumbnailItems();
        const activeIndex = thumbnails.findIndex((thumb) =>
          thumb.querySelector('button[aria-current="true"]')
        );
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;

        if (currentIndex <= 0) {
          this.prevThumbnailButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevThumbnailButton.removeAttribute('disabled');
        }

        if (currentIndex >= thumbnails.length - 1) {
          this.nextThumbnailButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextThumbnailButton.removeAttribute('disabled');
        }
      }

      setActiveDot(page) {
        if (!this.dotButtons?.length) return;

        this.dotButtons.forEach((dot, index) => {
          const isActive = index === page - 1;
          dot.classList.toggle('slider-counter__link--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      goToSlide(index) {
        const viewer = this.elements.viewer;
        if (!viewer?.slider) return;

        const slides = Array.from(viewer.querySelectorAll('[id^="Slide-"]')).filter(
          (element) => element.clientWidth > 0
        );
        const slide = slides[index];
        if (!slide) return;

        viewer.setSlidePosition(slide.offsetLeft);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target] button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateThumbnailNavButtons();

        if (!this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) {
          this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
        }

        // SliderComponent may re-disable arrows after scroll; keep active-slide state.
        requestAnimationFrame(() => this.updateThumbnailNavButtons());
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
